import { useState } from 'react';
import { ShieldAlert } from 'lucide-react';
import {
  ACTIVE_INCIDENTS,
  computeIncidentSummary,
  formatIncidentDateTime,
  generateIncidentId,
} from './incidents/mockIncidentData';
import EhsComplianceWidget from './incidents/EhsComplianceWidget';
import IncidentCreateForm from './incidents/IncidentCreateForm';
import ActiveIncidentsLedger from './incidents/ActiveIncidentsLedger';

export default function IncidentManagement() {
  const [incidents, setIncidents] = useState(ACTIVE_INCIDENTS);
  const summary = computeIncidentSummary(incidents);

  const handleCreate = ({ severity, description, actionTaken }) => {
    const newIncident = {
      id: generateIncidentId(incidents),
      severity,
      dateTime: formatIncidentDateTime(),
      description,
      actionTaken,
      status: 'open',
    };
    setIncidents((prev) => [newIncident, ...prev]);
  };

  const handleInvestigate = (id) => {
    setIncidents((prev) =>
      prev.map((inc) =>
        inc.id === id && inc.status === 'open'
          ? {
              ...inc,
              status: 'investigating',
              actionTaken: `${inc.actionTaken} · Investigation started`,
            }
          : inc,
      ),
    );
  };

  const handleClose = (id, closureNote) => {
    setIncidents((prev) =>
      prev.map((inc) =>
        inc.id === id
          ? {
              ...inc,
              status: 'closed',
              actionTaken: closureNote
                ? `${inc.actionTaken} · Closed: ${closureNote}`
                : `${inc.actionTaken} · Case closed by HSE`,
            }
          : inc,
      ),
    );
  };

  return (
    <section aria-label="Incident management and EHS compliance" className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-logi-grid pb-4">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-red-50 ring-1 ring-red-500/30">
              <ShieldAlert className="h-4 w-4 text-red-700" strokeWidth={1.75} />
            </div>
            <span className="text-xs font-semibold uppercase tracking-widest text-red-700/80">
              OGTS LogiCore™ · Safety &amp; Compliance Log
            </span>
          </div>
          <h2 className="text-xl font-bold tracking-tight text-logi-text sm:text-2xl">
            Incident Management
          </h2>
          <p className="mt-1 text-sm text-logi-muted">
            Log · investigate · close incidents · EHS metrics · HSE audit trail
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-logi-text0">
              Active Cases
            </p>
            <p className="font-mono text-lg font-bold text-red-700">
              {summary.open + summary.investigating}
            </p>
          </div>
          <div className="rounded-xl border border-orange-500/25 bg-orange-500/5 px-3 py-2 text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-logi-text0">
              Critical / Major
            </p>
            <p className="font-mono text-lg font-bold text-orange-700">
              {summary.critical + summary.major}
            </p>
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-logi-text0">
              Closed (Period)
            </p>
            <p className="font-mono text-lg font-bold text-emerald-700">{summary.closed}</p>
          </div>
        </div>
      </div>

      <EhsComplianceWidget />
      <IncidentCreateForm onSubmit={handleCreate} />
      <ActiveIncidentsLedger
        incidents={incidents}
        onInvestigate={handleInvestigate}
        onClose={handleClose}
      />
    </section>
  );
}
